/**
 * 사용·오류 기록 — "누가 언제 어떤 기능을 썼고, 무엇이 어떻게 실패했는가".
 *
 * 왜 화면에서 보내나: 서버가 볼 수 있는 것은 HTTP 요청뿐이다. "매핑 제안을 눌렀다", "노드를 지웠다",
 * "렌더 중 예외로 흰 화면이 됐다" 는 요청을 만들지 않거나 요청만 봐서는 무엇이었는지 알 수 없다.
 * 그래서 **기능 사용은 화면이 의미 단위로** 남기고, **실패한 요청은 서버 미들웨어가** 남긴다
 * (backend/app/main.py `_log_failed_requests`). 둘은 같은 파일 저장소로 모인다.
 *
 * 세 가지를 지킨다.
 *  1. **기능 이름은 정해진 목록에서만** 고른다(FEATURES). 클릭마다 남기면 노이즈가 신호를 덮는다.
 *  2. **로그가 앱을 방해하지 않는다.** 모아서 보내고, 실패하면 조용히 버린다. 재시도는 한 번뿐이다.
 *  3. **민감정보는 보내지 않는다.** 비밀번호·SQL 원문·데이터 행 값은 여기서 한 번, 서버에서 또 한 번 거른다
 *     (backend/app/services/event_log_store.py `_scrub`). 로그는 통째로 내보내지므로 이중으로 막는다.
 *
 * 관측 플랫폼(QPM) 연동은 이 모듈의 전송부에 싱크를 하나 더 다는 방식으로 나중에 붙인다 — docs/LOGGING.md.
 */
import { BE1_API } from '../config/api';
import { PERSISTENCE } from '../config/mode';

/** 기록할 기능 — 이 목록에 없는 것은 남기지 않는다(의미 단위 화이트리스트) */
export const FEATURES = [
  'app.start',
  'auth.login', 'auth.logout',
  'project.create', 'project.open', 'project.list',
  'workflow.create', 'workflow.open', 'workflow.save', 'workflow.deploy', 'workflow.run', 'workflow.stop',
  'workflow.duplicate', 'workflow.delete', 'workflow.list',
  'node.add', 'node.delete',
  'mapping.suggest', 'mapping.apply', 'quality.suggest', 'compare.generate', 'compare.ai',
  'datadef.save', 'datadef.delete',
  'connection.test', 'connection.limits',
  'driver.oracle.check', 'driver.oracle.save', 'driver.tibero.save',
  'file.upload', 'canvas.export', 'canvas.import',
  'project.export', 'project.import', 'workflow.bulkCreate',
  'assistant.ask', 'help.open', 'sample.refresh',
  'log.view', 'log.export',
  // 아래 셋은 오류 전용 — 사람이 부르는 기능이 아니라 사고 지점이다
  'api.failed', 'ui.crash', 'ui.unhandled',
] as const;

export type Feature = (typeof FEATURES)[number];

export interface LogEvent {
  kind: 'usage' | 'error';
  /** 사용자에게 보여 주는 짧은 오류 번호 — 신고와 기록을 잇는 유일한 끈 */
  errorId?: string;
  /** 어느 빌드에서 났나 (커밋 해시). 이것이 없으면 "어느 배포가 이 오류를 냈나" 에 답할 수 없다 */
  release?: string;
  /** 어느 화면 — id 는 서버가 `:id` 로 지운다 */
  route?: string;
  /** 브라우저 이름 + 주 버전 (UA 전문은 지문이라 싣지 않는다) */
  browser?: string;
  /** 오류 직전에 무엇을 했나 — 스택이 말해 주지 않는 "왜 거기까지 갔나" 를 메운다 */
  breadcrumbs?: Array<{ feature: string; target?: string; beforeMs: number }>;
  /** 화면이 아는 사용자 — 서버에 세션이 있으면 서버 값이 이긴다(event_log_store.normalize 주석 참고) */
  user?: string;
  ts: string;
  feature: Feature;
  mode: string;
  build: string;
  sessionId: string;
  ok: boolean;
  target?: string;
  durationMs?: number;
  detail?: Record<string, unknown>;
  error?: { message: string; name?: string; status?: number; where?: string; stack?: string };
}

const BUILD = process.env.REACT_APP_VERSION || '1.0';

/**
 * 이 화면이 어느 빌드인가 — 빌드할 때 커밋 해시를 넣는다(`REACT_APP_RELEASE`).
 * 없으면 버전으로 떨어진다. 없는 채로 두면 오류가 어느 배포에서 났는지 영영 모른다.
 */
const RELEASE = process.env.REACT_APP_RELEASE || BUILD;

/** 브라우저 이름 + 주 버전만. UA 전문은 지문이라 싣지 않는다. */
function describeBrowser(): string | undefined {
  try {
    const ua = navigator.userAgent;
    const m = /(Edg|OPR|Chrome|Firefox|Safari)\/(\d+)/.exec(ua);
    if (!m) return undefined;
    const name = { Edg: 'Edge', OPR: 'Opera' }[m[1] as 'Edg' | 'OPR'] || m[1];
    const os = /Windows/.test(ua) ? 'Windows' : /Mac OS/.test(ua) ? 'macOS'
      : /Android/.test(ua) ? 'Android' : /Linux/.test(ua) ? 'Linux' : '';
    return os ? `${name} ${m[2]} / ${os}` : `${name} ${m[2]}`;
  } catch {
    return undefined;
  }
}

/** 사용자가 눈으로 읽고 불러 줄 수 있는 모양. 헷갈리는 글자(0/O, 1/I)는 뺀다. */
const ID_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/**
 * 오류 번호를 **화면에서** 만든다.
 *
 * 서버에서 만들면 전송이 실패했을 때 사용자에게 보여 줄 번호가 없다 — 그런데 전송이 실패하는 상황이야말로
 * 사용자가 신고해야 하는 상황이다. 불투명한 난수라 화면이 만들어도 새는 것이 없다.
 */
function newErrorId(): string {
  const pick = (n: number) => Array.from({ length: n },
    () => ID_ALPHABET[Math.floor(Math.random() * ID_ALPHABET.length)]).join('');
  return `ERR-${pick(4)}-${pick(2)}`;
}
const FLUSH_MS = 5000;
const MAX_QUEUE = 25;      // 이만큼 쌓이면 기다리지 않고 보낸다
const MAX_PENDING = 200;   // 서버가 죽어 있을 때 메모리가 무한히 늘지 않게

/** 끌 수 있어야 한다 — 시험 환경이나 로그를 원치 않는 설치에서 REACT_APP_EVENT_LOG=0 */
export const LOG_ENABLED = (process.env.REACT_APP_EVENT_LOG || '1') !== '0';

/**
 * 오류 직전 행적 — 최근 사용 기록 몇 개를 들고 있다가 오류에 함께 싣는다.
 *
 * 스택은 **어디서** 터졌는지는 말해 주지만 **왜 거기까지 갔는지**는 말해 주지 않는다. 사용자의 한 줄
 * 신고가 그 자리를 메우지만 모두가 적어 주지는 않는다 — 이건 아무것도 묻지 않고 같은 자리를 메운다.
 *
 * 메모리에만 둔다. 전송은 오류가 날 때뿐이라 평소에는 아무 비용이 없다.
 */
const CRUMB_MAX = 10;
let crumbs: Array<{ feature: Feature; target?: string; at: number }> = [];

let currentUser: string | null = null;

/**
 * 로그에 붙일 사용자 이름을 알려 준다 — 세션이 확인된 뒤에 부른다.
 *
 * 왜 필요한가: QBase 모드는 인증이 QBase 쪽에 있어 우리 백엔드에 세션 쿠키가 없다. 서버가 사용자를
 * 알아낼 방법이 없으므로 화면이 알려 주지 않으면 모든 기록이 익명이 된다. 서버는 이 값을
 * `userVerified: false` 로 구분해 저장한다(스탠드얼론은 서버 세션이 이겨서 true).
 */
export function setLogUser(name: string | null): void {
  currentUser = name || null;
}

let queue: LogEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let sending = false;

/** 탭 하나를 구분하는 값 — 같은 사용자의 여러 창을 갈라 본다 */
function sessionId(): string {
  const KEY = 'openflow.logSession';
  try {
    let v = sessionStorage.getItem(KEY);
    if (!v) {
      v = `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem(KEY, v);
    }
    return v;
  } catch {
    return 's-nostorage';  // 프라이빗 모드 등 — 기록을 포기할 이유는 아니다
  }
}

const SECRET_KEY = /(pass|pwd|secret|token|credential|authorization|api[_-]?key)/i;
const DROP_KEY = /^(rows?|data|records?|values?|payload|preview|samples?|body|sql|query|statement|dsn)$/i;

/** 보내도 되는 모양으로 깎는다 — 서버와 같은 규칙(이중 방어) */
export function scrub(value: unknown, depth = 0): unknown {
  if (depth >= 4) return '…';
  if (value === null || value === undefined) return value;
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.length <= 500 ? value : `${value.slice(0, 500)}…(잘림)`;
  if (Array.isArray(value)) return value.slice(0, 20).map((v) => scrub(v, depth + 1));
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>).slice(0, 30)) {
      if (DROP_KEY.test(k)) out[k] = '(기록하지 않음)';
      else if (SECRET_KEY.test(k)) out[k] = '***';
      else out[k] = scrub(v, depth + 1);
    }
    return out;
  }
  return String(value);
}

function push(ev: LogEvent): void {
  if (!LOG_ENABLED) return;
  queue.push(ev);
  if (queue.length > MAX_PENDING) queue = queue.slice(-MAX_PENDING);
  if (queue.length >= MAX_QUEUE) { void flush(); return; }
  if (!timer) timer = setTimeout(() => { void flush(); }, FLUSH_MS);
}

function base(feature: Feature, kind: 'usage' | 'error'): LogEvent {
  return {
    kind, feature, ts: new Date().toISOString(), mode: PERSISTENCE, build: BUILD,
    sessionId: sessionId(), ok: kind !== 'error',
    release: RELEASE,
    ...(typeof window !== 'undefined' ? { route: window.location.pathname } : {}),
    ...(describeBrowser() ? { browser: describeBrowser() } : {}),
    ...(currentUser ? { user: currentUser } : {}),
  };
}

/** 기능을 썼다 — 성공·실패 여부와 걸린 시간을 함께 남길 수 있다 */
export function logUsage(
  feature: Feature,
  opts: { target?: string; durationMs?: number; detail?: Record<string, unknown> } = {},
): void {
  const ev = base(feature, 'usage');
  if (opts.target) ev.target = String(opts.target).slice(0, 200);
  if (typeof opts.durationMs === 'number') ev.durationMs = Math.round(opts.durationMs);
  if (opts.detail) ev.detail = scrub(opts.detail) as Record<string, unknown>;
  push(ev);
  // 오류가 나면 이 자취가 함께 실린다
  crumbs.push({ feature, target: ev.target, at: Date.now() });
  if (crumbs.length > CRUMB_MAX) crumbs = crumbs.slice(-CRUMB_MAX);
}

/** 무엇이 어떻게 실패했는가 — 메시지와 스택을 남긴다 */
export function logError(
  feature: Feature,
  err: unknown,
  opts: { target?: string; where?: string; status?: number; detail?: Record<string, unknown> } = {},
): string {
  const e = err as { message?: string; name?: string; stack?: string; status?: number };
  const ev = base(feature, 'error');
  ev.errorId = newErrorId();
  ev.error = {
    message: String(e?.message ?? err ?? '(내용 없음)').slice(0, 2000),
    name: e?.name,
    status: opts.status ?? e?.status,
    where: opts.where,
    stack: typeof e?.stack === 'string' ? e.stack.slice(0, 8000) : undefined,
  };
  if (opts.target) ev.target = String(opts.target).slice(0, 200);
  if (opts.detail) ev.detail = scrub(opts.detail) as Record<string, unknown>;
  if (crumbs.length) {
    // 절대 시각이 아니라 **오류로부터 몇 ms 전**으로 싣는다 — 브라우저 시계가 틀어져 있어도 순서와
    // 간격은 맞고, 값도 짧다.
    const now = Date.now();
    ev.breadcrumbs = crumbs.map((c) => ({
      feature: c.feature,
      ...(c.target ? { target: c.target } : {}),
      beforeMs: Math.max(0, now - c.at),
    }));
  }
  push(ev);
  // **오류는 모아 두지 않는다.** 그 뒤에 화면이 죽거나 사용자가 새로고침하면 버퍼째 사라지는데,
  // 하필 그때 난 오류가 가장 알고 싶은 것이다. (사용 기록은 그대로 모아서 보낸다)
  void flush();
  return ev.errorId!;
}

/**
 * 사용자가 "무엇을 하려던 중이었나" 를 적어 보낸다 — **자동 수집이 얻을 수 없는 유일한 정보**다.
 *
 * 오류 번호로 기록에 묶인다. 실패해도 조용히 지나간다(신고하다 또 오류가 나면 앞뒤가 바뀐다).
 */
export async function reportError(errorId: string, note: string): Promise<boolean> {
  try {
    const res = await fetch(`${BE1_API}/api/logs/report`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ errorId, note: String(note || '').slice(0, 1000) }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * 모아 둔 것을 보낸다.
 *
 * be1Fetch 를 쓰지 않는 이유: be1Fetch 는 실패를 이 모듈에 기록한다. 그대로 쓰면 전송 실패가 새 기록을 낳고
 * 그것이 다시 전송을 부르는 고리가 된다. 여기서는 맨 fetch 로 한 번만 시도하고, 실패하면 조용히 되돌린다.
 */
export async function flush(): Promise<void> {
  if (timer) { clearTimeout(timer); timer = null; }
  if (sending || queue.length === 0) return;
  const batch = queue;
  queue = [];
  sending = true;
  try {
    const res = await fetch(`${BE1_API}/api/logs`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events: batch }),
    });
    if (!res.ok) throw new Error(String(res.status));
  } catch {
    // 서버가 잠깐 죽었을 수 있다 — 되돌려 두고 다음 주기에 함께 보낸다(상한은 MAX_PENDING)
    queue = [...batch, ...queue].slice(-MAX_PENDING);
  } finally {
    sending = false;
  }
}

/** 창을 닫는 중에도 마지막 배치를 보낸다 — 브라우저는 이때 fetch 를 끊는다 */
function flushOnExit(): void {
  if (queue.length === 0) return;
  const body = JSON.stringify({ events: queue });
  try {
    if (navigator.sendBeacon?.(`${BE1_API}/api/logs`, new Blob([body], { type: 'application/json' }))) {
      queue = [];
      return;
    }
  } catch { /* 아래 fetch 로 한 번 더 시도 */ }
  void flush();
}

let installed = false;

/**
 * 전역 오류 후크 — 화면 어디서 터져도 기록이 남게 한다.
 *
 * ResizeObserver 루프 경고는 제외한다. 실제 결함이 아니고(utils/ResizeObserverErrorFix 가 이미 억제한다)
 * 스크롤 한 번에 수십 건씩 나서 그대로 두면 진짜 오류가 묻힌다.
 */
export function installGlobalErrorHandlers(): void {
  if (installed || !LOG_ENABLED || typeof window === 'undefined') return;
  installed = true;

  // 앱이 떴다는 기록도 여기서 남긴다 — 개발 모드의 StrictMode 는 효과를 두 번 부르므로
  // 화면 쪽에서 남기면 같은 기동이 두 줄이 된다(실측).
  logUsage('app.start');

  window.addEventListener('error', (e: ErrorEvent) => {
    if (e.message?.includes('ResizeObserver loop')) return;
    logError('ui.unhandled', e.error || new Error(e.message), {
      where: `${e.filename || ''}:${e.lineno || 0}`,
    });
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    logError('ui.unhandled', e.reason, { where: 'promise' });
  });

  window.addEventListener('pagehide', flushOnExit);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushOnExit();
  });
}

/** 시험용 — 큐를 비운다 */
export function _resetForTest(): void {
  queue = [];
  crumbs = [];
  if (timer) { clearTimeout(timer); timer = null; }
  sending = false;
}

/** 시험용 — 아직 보내지 않은 것 */
export function _pendingForTest(): LogEvent[] {
  return [...queue];
}
